<script lang="ts">
    // Svelte
    import { get } from 'svelte/store';
    import { onMount } from 'svelte';

    // Shared Components
    import ProductContextMenu from '@amp/web-app-components/src/components/ProductContextMenu/ProductContextMenu.svelte';
    import { throttle } from '@amp/web-app-components/src/utils/throttle';

    // Types
    import type { ContextualMenuButtonStyle } from '@amp/web-app-components/src/components/ProductContextMenu/ProductContextMenu.svelte';
    import { type ContextualMenuPerformAction } from '@jet-app/music-classical/action-model/contextual-menu/PerformAction';
    import type { ContextMenuItemAction } from '@jet-app/music-classical/action-model/contextual-menu/types';

    // Utils
    import {
        getContextualMenu,
        type ContextualMenuItem,
    } from '~/utils/contextual-menu';
    import { getJet } from '~/jet';
    import { canShareLink, shareLinkOrCopy, copyLink } from '~/utils/share';
    import { makeModifyPlaylistAction } from '@jet-app/music-classical/action-model/ModifyPlaylistAction';

    // Constants
    import { ICONS } from '~/constants/product-context-menu';

    // Stores
    import { getI18n } from '~/stores/i18n';
    import { showConfirm } from '@amp/web-app-components/src/stores/warning';
    import {
        editPlaylistData,
        newPlaylistData,
        playlists,
        showEditPlaylistModal,
        showNewPlaylistModal,
    } from '~/stores/playlists';

    const jet = getJet();
    const i18n = getI18n();
    const contextualMenu = getContextualMenu();
    const THROTTLE_TIME_MOUSE_OVER = 3000;

    export let buttonStyle: ContextualMenuButtonStyle = 'platter';
    export let hasMaterial: boolean = true;
    export let contextMenuAction: any;
    export let contextualMenuButtonElement: HTMLElement = null;
    // Updates contextual menu config to render the <amp-contextual-menu />
    // element somewhere other than the default of document.body
    export let parentElement: HTMLElement = null;

    // Initial Empty Config with Loading Item
    $: config = { items: [] };

    const updateLoadingItem = () => {
        config = {
            items: [
                {
                    label: $i18n.t(
                        'ASE.Web.MusicClassical.ContextualMenu.Loading',
                    ),
                    icon: ICONS.LoadingIcon,
                } as ContextualMenuItem,
            ],
        };
    };

    const updateConfig = async () => {
        await jet.perform(contextMenuAction);
    };

    const handleMouseOver = async () => {
        await updateConfig();
    };

    const throttledMouseOver = throttle(
        handleMouseOver,
        THROTTLE_TIME_MOUSE_OVER,
    );

    const handlePerformAction = async (
        performAction: ContextualMenuPerformAction,
    ) => {
        await jet.perform(performAction);
    };

    const handleContextMenuVisibilityChange = (event: CustomEvent) => {
        if (event.detail?.open) {
            // Initially set the loading item and if promise resolves then show actual items
            updateLoadingItem();

            // Get Menu items from the getAllMediaItemsConfig method which returns from the LRU
            contextualMenu
                .getAllMediaItemsConfig(contextMenuAction.url)
                .then((updatedConfig) => {
                    config = {
                        items: updatedConfig.items.map(
                            (item: ContextualMenuItem) => {
                                return {
                                    ...item,
                                    // Sub Menu Items populated for items like Add to Playlist
                                    ...getSubMenuForAddToPlaylist(item),

                                    // For Share Item add a success message if native sharing not supported
                                    ...getShareItemSuccess(item),
                                    action: () => getActionForMenuItem(item),
                                };
                            },
                        ),
                    };
                });
        }
    };

    const getShareItemSuccess = (item: ContextualMenuItem) => {
        const action = item?.action as unknown as ContextualMenuPerformAction;
        // Also add success message to the shared menu if native sharing is not supported
        if (
            (action?.type === 'share' && !canShareLink(action.url)) ||
            action?.type === 'copy'
        ) {
            return {
                clickTransition: {
                    newText: $i18n.t(
                        'ASE.Web.MusicClassical.ContextualMenu.Copied.Success',
                    ),
                    hideIcon: true,
                    closeAfter: 1000,
                },
            };
        }

        return {};
    };

    const getActionForMenuItem = async (item: ContextualMenuItem) => {
        const action = item.action as unknown as ContextMenuItemAction;

        // Remove existing items from cache once action is being performed
        contextualMenu.clearItems(contextMenuAction.url);

        switch (action?.type) {
            case 'confirm-delete':
                const shouldDelete = await showConfirm({
                    header: action?.title,
                    message: action?.text,
                    actionText: action?.deleteButton?.title,
                    confirmText: action?.cancelButton?.title,
                });
                if (shouldDelete) {
                    handlePerformAction(
                        action?.deleteButton
                            ?.action as unknown as ContextualMenuPerformAction,
                    );
                }
                break;

            case 'edit-playlist':
                editPlaylistData.set(action?.payload);
                showEditPlaylistModal.set(true);
                break;

            case 'share':
                shareLinkOrCopy({ url: action?.url });
                break;

            case 'copy':
                copyLink({ url: action?.url });
                break;

            case 'addToPlaylist':
                break;

            default:
                handlePerformAction(action);
                break;
        }
    };

    const getSubMenuForAddToPlaylist = (item: ContextualMenuItem) => {
        const action = item.action as unknown as ContextMenuItemAction;
        if (action?.type === 'addToPlaylist') {
            const userPlaylists =
                get(playlists)?.filter((playlist) => playlist?.canEdit) || [];
            const allPlaylistsItems = userPlaylists.map((playlist) => ({
                id: playlist.id?.resourceId,
                label: playlist.label,
                icon: ICONS.PlaylistIcon,
                action: async () => {
                    // Remove existing items from cache once action is being performed
                    contextualMenu.clearItems(contextMenuAction.url);

                    await jet.perform(
                        makeModifyPlaylistAction('addToPlaylist', {
                            songs: (
                                action.payload as unknown as {
                                    songs: string[];
                                }
                            )?.songs,
                            playlistId: playlist.id?.resourceId,
                            name: playlist.label,
                        }),
                    );
                },
            }));

            return {
                styles: {
                    ['--ctxmenu-submenu-min-width']: '185px',
                    ['--ctxmenu-submenu-max-width']: '350px',
                    ['--ctxmenu-submenu-max-height']: '350px',
                },
                // Todo: Ask Classical API to return a flag on playlists which are User Editable Playlists and filter this list by that flag
                // rdar://140428863 ([WEB] Add or pass canEdit flag on all Playlists for the Sidebar so we can filter editable playlists in Add to Playlist Context Menu)
                children: [
                    {
                        label: $i18n.t(
                            'ASE.Web.MusicClassical.ContextualMenu.NewPlaylist',
                        ),
                        icon: ICONS.AddToLibraryIcon,
                        action: () => {
                            // Remove existing items from cache once action is being performed
                            contextualMenu.clearItems(contextMenuAction.url);

                            newPlaylistData.set({
                                songs: (
                                    action.payload as unknown as {
                                        songs: string[];
                                    }
                                )?.songs,
                            });
                            showNewPlaylistModal.set(true);
                        },
                    },
                    ...allPlaylistsItems,
                ],
            };
        }

        return {};
    };

    onMount(() => {
        /**
         * Required for triggering a fetch of items from LRU Cache if
         * explicitItems is provided by the IntentController and this component is passed with
         * those to render onMount
         */
        if (contextMenuAction?.explicitItems) {
            handleContextMenuVisibilityChange(
                new CustomEvent('', { detail: { open: true } }),
            );
        }
    });
</script>

{#if contextMenuAction}
    <ProductContextMenu
        translateFn={$i18n.t}
        {config}
        {buttonStyle}
        {hasMaterial}
        on:triggerFocus={throttledMouseOver}
        on:mouseover={throttledMouseOver}
        on:contextMenuVisibilityChange={handleContextMenuVisibilityChange}
        bind:contextualMenuButtonElement
        bind:parentElement
    />
{/if}
